import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    InputLabel,
    LinearProgress,
    Stack,
    Typography,
} from '@mui/material';
import { withStyles } from '@mui/styles';
// eslint-disable-next-line no-restricted-imports
import { Controller, useFormContext, useWatch } from 'react-hook-form-v7';
import moment from 'moment';
import { COLORS } from '../../../utils/Application_Constants';
import ConnectedDatePicker from '../ProcessedTimesheets/ConnectedDatePicker';
import Switch from '../../../Shared/Components/Switch';
import { formatUtcDate } from '../../../utils/formatDate';
import {
    ACCOUNTING_PERIOD_WARNING,
    getMinimumAllowedInvoiceDate,
    getMaximumAllowedInvoiceDate,
    hasPriorInvoiceOrPostingDate,
    validateInvoiceDateRange,
} from '../../Utils/validateInvoiceDates';

const styles = () => ({
    datePicker: {
        '& .MuiPickersSectionList-root': {
            padding: '13px 0',
            fontWeight: 'normal',
            fontSize: 14,
        },
        '& .MuiPickersOutlinedInput-notchedOutline': {
            borderColor: COLORS.LT_MIDNIGHT10,
        },
        '& .MuiFormHelperText-root': {
            marginLeft: 0,
            position: 'absolute',
            top: '100%',
        },
        '&.MuiPickersTextField-root': {
            marginBottom: 0,
        },
    },
    formLabel: {
        marginTop: 12,
        '& .MuiTypography-root': {
            fontSize: 14,
        },
    },
});

const InvoiceConfirmationModal = ({
    classes,
    open,
    onClose,
    title,
    showInvoiceForm,
    bodyMessage,
    onSubmit,
    isLoading,
    progress,
    error,
    actionButtonLabel,
    actionButtonColor = 'primary',
    showDoNotSendToClient = true,
    invoiceThroughDate,
    preInvoiceDates = [],
    selectedPreInvoiceCount = 0,
}) => {
    const { control } = useFormContext();
    const selectedInvoiceDate = useWatch({ control, name: 'invoiceDate' });
    const selectedPostingDate = useWatch({ control, name: 'postingDate' });
    const minAllowedDate = getMinimumAllowedInvoiceDate();
    const maxAllowedDate = getMaximumAllowedInvoiceDate();
    const showAccountingPeriodWarning =
        showInvoiceForm && hasPriorInvoiceOrPostingDate(selectedInvoiceDate, selectedPostingDate);

    // Pre-invoice dates are stored/displayed as UTC calendar dates, while the picker
    // value is a local moment; compare calendar dates the same way the grid renders them.
    const pdfRegenerationCount =
        showInvoiceForm && selectedInvoiceDate
            ? preInvoiceDates.filter(
                  (preInvoiceDate) =>
                      preInvoiceDate &&
                      moment.utc(preInvoiceDate).format('YYYY-MM-DD') !==
                          selectedInvoiceDate.format('YYYY-MM-DD')
              ).length
            : 0;

    // Cross-page select-all can include pre-invoices that are not loaded on the current
    // page, so their dates are unknown here. An exact mismatch count would be misleading
    // (it could show zero); fall back to a generic notice instead.
    const hasSelectionsWithUnknownDates =
        showInvoiceForm && selectedPreInvoiceCount > preInvoiceDates.length;

    const progressValue =
        progress?.total && progress.total > 0
            ? Math.round((progress.current / progress.total) * 100)
            : 0;

    return (
        <Dialog
            open={open}
            onClose={isLoading ? null : onClose}
            maxWidth="sm"
            slotProps={{ paper: { sx: { borderRadius: '8px', padding: '30px' } } }}
        >
            <DialogTitle>
                <Typography variant="heading3" sx={{ color: COLORS.BLACK }}>
                    {title}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <form onSubmit={onSubmit}>
                    <Typography variant="body2">{bodyMessage}</Typography>
                    {!!invoiceThroughDate && (
                        <Alert severity="warning" sx={{ mt: 2 }} icon={false}>
                            <AlertTitle>Invoice Through Date Filter Applied</AlertTitle>
                            <ul style={{ marginTop: 0, marginBottom: 0, paddingLeft: '20px' }}>
                                <li>
                                    Time and Expense line items through{' '}
                                    {formatUtcDate(invoiceThroughDate)} will be invoiced.
                                </li>
                                <li>
                                    Remaining line items will be available for future invoicing.
                                </li>
                                <li>
                                    ⚠️ Processed Timesheets invoiced across multiple invoicing
                                    cycles may require additional review when corrections occur.
                                </li>
                            </ul>
                        </Alert>
                    )}
                    {showInvoiceForm && (
                        <>
                            <Stack
                                sx={{
                                    border: `1px solid ${COLORS.LT_MIDNIGHT10} `,
                                    mt: 2,
                                    p: 1,
                                    borderRadius: '8px',
                                }}
                                gap={2}
                            >
                                <Typography
                                    variant="heading5"
                                    sx={{ fontWeight: 'bold', color: COLORS.BLACK }}
                                >
                                    Verify Dates
                                </Typography>
                                <Typography variant="body2">
                                    The Posting Date affects your accounting records, and the
                                    Invoice Date is the official client issue date. Please confirm.
                                </Typography>
                                <Stack direction="row" gap={2}>
                                    <Box sx={{ mr: 2 }}>
                                        <InputLabel
                                            sx={{ fontSize: 14, mb: 1 }}
                                            id="rate-type-label"
                                        >
                                            Posting Date
                                        </InputLabel>
                                        <ConnectedDatePicker
                                            control={control}
                                            name="postingDate"
                                            pickerProps={{
                                                className: classes.datePicker,
                                                format: 'MM/DD/YYYY',
                                                minDate: minAllowedDate,
                                                maxDate: maxAllowedDate,
                                            }}
                                            rules={{
                                                required: 'Posting Date is required.',
                                                validate: (selectedDate) =>
                                                    validateInvoiceDateRange(
                                                        selectedDate,
                                                        'Posting Date'
                                                    ),
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ mr: 2 }}>
                                        <InputLabel
                                            sx={{ fontSize: 14, mb: 1 }}
                                            id="rate-type-label"
                                        >
                                            Invoice Date
                                        </InputLabel>
                                        <ConnectedDatePicker
                                            control={control}
                                            name="invoiceDate"
                                            pickerProps={{
                                                className: classes.datePicker,
                                                format: 'MM/DD/YYYY',
                                                minDate: minAllowedDate,
                                                maxDate: maxAllowedDate,
                                            }}
                                            rules={{
                                                required: 'Invoice Date is required.',
                                                validate: (selectedDate) =>
                                                    validateInvoiceDateRange(
                                                        selectedDate,
                                                        'Invoice Date'
                                                    ),
                                            }}
                                        />
                                    </Box>
                                </Stack>
                                {showAccountingPeriodWarning && (
                                    <Alert
                                        severity="warning"
                                        icon={false}
                                        data-testid="accounting-period-warning"
                                    >
                                        <AlertTitle>{ACCOUNTING_PERIOD_WARNING.title}</AlertTitle>
                                        {ACCOUNTING_PERIOD_WARNING.body}
                                    </Alert>
                                )}
                                {hasSelectionsWithUnknownDates && (
                                    <Alert
                                        severity="info"
                                        icon={false}
                                        data-testid="pdf-regeneration-generic-notice"
                                    >
                                        Pre-invoices whose pre-invoice date differs from the
                                        selected Invoice Date will have their invoice PDF(s)
                                        regenerated to show the selected Invoice Date. If PDF
                                        regeneration fails, the pre-invoice remains on the
                                        Pre-Invoiced tab and can be invoiced again.
                                    </Alert>
                                )}
                                {!hasSelectionsWithUnknownDates && pdfRegenerationCount > 0 && (
                                    <Alert
                                        severity="info"
                                        icon={false}
                                        data-testid="pdf-regeneration-notice"
                                    >
                                        The selected Invoice Date differs from the pre-invoice date
                                        for {pdfRegenerationCount} selected pre-invoice(s). Their
                                        invoice PDF(s) will be regenerated to show the selected
                                        Invoice Date. If PDF regeneration fails, the pre-invoice
                                        remains on the Pre-Invoiced tab and can be invoiced again.
                                    </Alert>
                                )}
                            </Stack>
                            {showDoNotSendToClient && (
                                <Controller
                                    control={control}
                                    name="doNotSendToClient"
                                    render={({ field }) => (
                                        <FormControlLabel
                                            name="doNotSendToClient"
                                            className={classes.formLabel}
                                            control={
                                                <Switch
                                                    checked={field.value}
                                                    onChange={field.onChange}
                                                />
                                            }
                                            label="Do not send to client"
                                        />
                                    )}
                                />
                            )}
                        </>
                    )}
                    {isLoading && progress?.total > 0 && (
                        <Stack sx={{ mt: 2 }} gap={1}>
                            <Typography variant="body2" sx={{ fontSize: 12 }}>
                                Processing {progress.current} of {progress.total} client(s)
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={progressValue}
                                sx={{ borderRadius: 999 }}
                            />
                        </Stack>
                    )}
                    <Stack direction="row" sx={{ mt: 4 }}>
                        <Button
                            variant="lt-admin-outlined"
                            color="primary"
                            sx={{ mr: 2 }}
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="lt-admin-contained"
                            color={actionButtonColor}
                            type="submit"
                            loading={isLoading}
                            sx={{ ml: 'auto' }}
                        >
                            {actionButtonLabel}
                        </Button>
                    </Stack>
                    {error?.message && (
                        <Stack sx={{ mt: 2 }}>
                            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                {error.message}
                            </Typography>
                            {error?.errors && error?.errors?.length > 0 && (
                                <ul style={{ marginTop: 0, paddingLeft: '20px' }}>
                                    {error.errors.map((lineError, index) => (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <li key={index} style={{ color: COLORS.MILANO_RED }}>
                                            <Typography
                                                variant="body2"
                                                color="error"
                                                sx={{ fontSize: 12 }}
                                            >
                                                {lineError}
                                            </Typography>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Stack>
                    )}
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default withStyles(styles)(InvoiceConfirmationModal);
